import React, { useState } from 'react';
import { X, Edit, Copy, Archive, ExternalLink, Eye } from 'lucide-react';
import { Chart } from '../components/Chart';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
interface BackofferDetailProps {
  offer: any;
  onClose: () => void;
  onEdit: () => void;
  onClone: () => void;
  onArchive: () => void;
}
export const BackofferDetail: React.FC<BackofferDetailProps> = ({
  offer,
  onClose,
  onEdit,
  onClone,
  onArchive
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('7d');
  // Prepare chart data for segments
  const segmentPerformanceData = Object.entries(offer.performance.segmentPerformance).map(([key, value]: [string, any]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' '),
    cr: value.cr * 100,
    epc: value.epc,
    rpm: value.rpm
  }));
  // Prepare chart data for geos
  const geoPerformanceData = Object.entries(offer.performance.geoPerformance).map(([key, value]: [string, any]) => ({
    name: key.toUpperCase(),
    cr: value.cr * 100,
    epc: value.epc,
    rpm: value.rpm
  }));
  // Prepare chart data for trends
  const trendData = offer.performance.trendData.dates.map((date: string, index: number) => ({
    date,
    revenue: offer.performance.trendData.revenue[index],
    epc: offer.performance.trendData.epc[index],
    cr: offer.performance.trendData.cr[index] * 100
  }));
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11359">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-11360">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-11361">
          {offer.name}
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-11362">
          <X size={20} data-id="element-11363" />
        </button>
      </div>
      <div className="p-6" data-id="element-11364">
        <div className="flex justify-between items-center mb-6" data-id="element-11365">
          <div className="flex space-x-3" data-id="element-11366">
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${offer.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-11367">
              {offer.status === 'active' ? 'Active' : 'Inactive'}
            </span>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400" data-id="element-11368">
              {offer.verticalId.charAt(0).toUpperCase() + offer.verticalId.slice(1)}
            </span>
          </div>
          <div className="flex space-x-2" data-id="element-11369">
            <select className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-sm" value={period} onChange={e => setPeriod(e.target.value)} data-id="element-11370">
              <option value="today" data-id="element-11371">Today</option>
              <option value="yesterday" data-id="element-11372">Yesterday</option>
              <option value="7d" data-id="element-11373">Last 7 days</option>
              <option value="30d" data-id="element-11374">Last 30 days</option>
              <option value="90d" data-id="element-11375">Last 90 days</option>
            </select>
            <button onClick={onEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors flex items-center" data-id="element-11376">
              <Edit size={16} className="mr-2" data-id="element-11377" />
              Edit
            </button>
            <button onClick={onClone} className="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors flex items-center" data-id="element-11378">
              <Copy size={16} className="mr-2" data-id="element-11379" />
              Clone
            </button>
            <button onClick={onArchive} className="px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors flex items-center" data-id="element-11380">
              <Archive size={16} className="mr-2" data-id="element-11381" />
              Archive
            </button>
          </div>
        </div>
        <div className="mb-6" data-id="element-11382">
          <Tabs defaultValue="overview" data-id="element-11383">
            <TabsList data-id="element-11384">
              <TabsTrigger value="overview" data-id="element-11385">Overview</TabsTrigger>
              <TabsTrigger value="performance" data-id="element-11386">Performance</TabsTrigger>
              <TabsTrigger value="targeting" data-id="element-11387">Targeting</TabsTrigger>
              <TabsTrigger value="creatives" data-id="element-11388">Creatives</TabsTrigger>
              <TabsTrigger value="tracking" data-id="element-11389">Tracking</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-6" data-id="element-11390">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11391">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11392">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11393">
                    Offer Details
                  </h3>
                  <div className="space-y-4" data-id="element-11394">
                    <div data-id="element-11395">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11396">
                        Partner
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11397">
                        {offer.partnerName}
                      </p>
                    </div>
                    <div data-id="element-11398">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11399">
                        Vertical
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11400">
                        {offer.verticalId.charAt(0).toUpperCase() + offer.verticalId.slice(1)}
                      </p>
                    </div>
                    <div data-id="element-11401">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11402">
                        Description
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11403">
                        {offer.description}
                      </p>
                    </div>
                    <div data-id="element-11404">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11405">
                        Base URL
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white break-all" data-id="element-11406">
                        {offer.baseUrl}
                      </p>
                    </div>
                    <div data-id="element-11407">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11408">
                        Payout Model
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11409">
                        {offer.payoutModelId.toUpperCase()} ($
                        {offer.payoutAmount.toFixed(2)})
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11410">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11411">
                    Performance Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-11412">
                    <div data-id="element-11413">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11414">
                        Impressions
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11415">
                        {offer.performance.impressions.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11416">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11417">
                        Clicks
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11418">
                        {offer.performance.clicks.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11419">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11420">
                        Conversions
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11421">
                        {offer.performance.conversions.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11422">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11423">
                        Revenue
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11424">
                        ${offer.performance.revenue.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11425">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11426">
                        EPC
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11427">
                        ${offer.performance.epc.toFixed(2)}
                      </p>
                    </div>
                    <div data-id="element-11428">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11429">
                        Conversion Rate
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11430">
                        {(offer.performance.cr * 100).toFixed(2)}%
                      </p>
                    </div>
                    <div data-id="element-11431">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11432">
                        RPM
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11433">
                        ${offer.performance.rpm.toFixed(2)}
                      </p>
                    </div>
                    <div data-id="element-11434">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11435">
                        Refund Rate
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-11436">
                        {(offer.performance.refundRate * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 mt-6" data-id="element-11437">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11438">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11439">
                    Performance Trend
                  </h3>
                  <div className="h-80" data-id="element-11440">
                    <Chart title="" data={trendData} type="line" height={300} yAxisLabel="Value" xAxisDataKey="date" colors={['#10b981', '#3b82f6', '#8b5cf6']} dataKeys={['revenue', 'epc', 'cr']} data-id="element-11441" />
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="performance" className="pt-6" data-id="element-11442">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11443">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11444">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11445">
                    Segment Performance
                  </h3>
                  <div className="h-80" data-id="element-11446">
                    <Chart title="" data={segmentPerformanceData} type="bar" height={300} yAxisLabel="Value" colors={['#10b981', '#3b82f6', '#8b5cf6']} dataKeys={['cr', 'epc', 'rpm']} data-id="element-11447" />
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11448">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11449">
                    GEO Performance
                  </h3>
                  <div className="h-80" data-id="element-11450">
                    <Chart title="" data={geoPerformanceData} type="bar" height={300} yAxisLabel="Value" colors={['#10b981', '#3b82f6', '#8b5cf6']} dataKeys={['cr', 'epc', 'rpm']} data-id="element-11451" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 mt-6" data-id="element-11452">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11453">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11454">
                    Performance Details
                  </h3>
                  <div className="overflow-x-auto" data-id="element-11455">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11456">
                      <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-11457">
                        <tr data-id="element-11458">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11459">
                            Segment
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11460">
                            Conversion Rate
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11461">
                            EPC
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-11462">
                            RPM
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11463">
                        {Object.entries(offer.performance.segmentPerformance).map(([key, value]: [string, any]) => <tr key={key} data-id="element-11464">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-11465">
                              {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-11466">
                              {(value.cr * 100).toFixed(2)}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-11467">
                              ${value.epc.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-11468">
                              ${value.rpm.toFixed(2)}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="targeting" className="pt-6" data-id="element-11469">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11470">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11471">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11472">
                    Target GEOs
                  </h3>
                  <div className="space-y-4" data-id="element-11473">
                    <div data-id="element-11474">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11475">
                        Included GEOs
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11476">
                        {offer.targeting.geos.map((geo: string) => <span key={geo} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded" data-id="element-11477">
                            {geo.toUpperCase()}
                          </span>)}
                      </div>
                    </div>
                    {offer.targeting.excludedGeos && offer.targeting.excludedGeos.length > 0 && <div data-id="element-11478">
                          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11479">
                            Excluded GEOs
                          </h4>
                          <div className="mt-1 flex flex-wrap gap-1" data-id="element-11480">
                            {offer.targeting.excludedGeos.map((geo: string) => <span key={geo} className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 rounded" data-id="element-11481">
                                {geo.toUpperCase()}
                              </span>)}
                          </div>
                        </div>}
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11482">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11483">
                    Device Targeting
                  </h3>
                  <div className="space-y-4" data-id="element-11484">
                    <div data-id="element-11485">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11486">
                        Devices
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11487">
                        {offer.targeting.devices.map((device: string) => <span key={device} className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 rounded" data-id="element-11488">
                            {device.charAt(0).toUpperCase() + device.slice(1)}
                          </span>)}
                      </div>
                    </div>
                    <div data-id="element-11489">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11490">
                        Operating Systems
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11491">
                        {offer.targeting.operatingSystems.map((os: string) => <span key={os} className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded" data-id="element-11492">
                            {os.charAt(0).toUpperCase() + os.slice(1)}
                          </span>)}
                      </div>
                    </div>
                    <div data-id="element-11493">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11494">
                        Browsers
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11495">
                        {offer.targeting.browsers.map((browser: string) => <span key={browser} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 rounded" data-id="element-11496">
                            {browser.charAt(0).toUpperCase() + browser.slice(1)}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-11497">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11498">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11499">
                    Segment Targeting
                  </h3>
                  <div className="space-y-4" data-id="element-11500">
                    <div data-id="element-11501">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11502">
                        Target Segments
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11503">
                        {offer.segmentTargeting.segments.map((segment: string) => <span key={segment} className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-11504">
                              {segment.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </span>)}
                      </div>
                    </div>
                    <div data-id="element-11505">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11506">
                        Fallback Type
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11507">
                        {offer.segmentTargeting.fallbackType.charAt(0).toUpperCase() + offer.segmentTargeting.fallbackType.slice(1)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11508">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11509">
                    Time & Language Targeting
                  </h3>
                  <div className="space-y-4" data-id="element-11510">
                    <div data-id="element-11511">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11512">
                        Languages
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11513">
                        {offer.targeting.languages.map((language: string) => <span key={language} className="px-2 py-1 text-xs bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400 rounded" data-id="element-11514">
                            {language === 'en' ? 'English' : language === 'de' ? 'German' : language === 'fr' ? 'French' : language}
                          </span>)}
                      </div>
                    </div>
                    <div data-id="element-11515">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11516">
                        Time of Day
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11517">
                        {offer.targeting.timeOfDay ? `${offer.targeting.timeOfDay.start}:00 - ${offer.targeting.timeOfDay.end}:00` : 'All day'}
                      </p>
                    </div>
                    <div data-id="element-11518">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11519">
                        Days of Week
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11520">
                        {offer.targeting.daysOfWeek.map((day: number) => <span key={day} className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 rounded" data-id="element-11521">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="creatives" className="pt-6" data-id="element-11522">
              <div className="grid grid-cols-1 gap-6" data-id="element-11523">
                {offer.creatives && offer.creatives.map((creative: any) => <div key={creative.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11524">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-11525">
                        <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-11526">
                          Creative: {creative.headline}
                        </h3>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${creative.approved ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'}`} data-id="element-11527">
                          {creative.approved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                      <div className="p-6" data-id="element-11528">
                        <div className="flex flex-col md:flex-row gap-6" data-id="element-11529">
                          <div className="md:w-1/3" data-id="element-11530">
                            <div className="aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-700 rounded-md overflow-hidden" data-id="element-11531">
                              {creative.url ? <img src={creative.url} alt={creative.headline} className="object-cover w-full h-full" data-id="element-11532" /> : <div className="flex items-center justify-center h-full" data-id="element-11533">
                                  <span className="text-gray-500 dark:text-gray-400" data-id="element-11534">
                                    No image available
                                  </span>
                                </div>}
                            </div>
                          </div>
                          <div className="md:w-2/3" data-id="element-11535">
                            <div className="space-y-4" data-id="element-11536">
                              <div data-id="element-11537">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11538">
                                  Type
                                </h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11539">
                                  {creative.type.charAt(0).toUpperCase() + creative.type.slice(1)}
                                </p>
                              </div>
                              <div data-id="element-11540">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11541">
                                  Headline
                                </h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11542">
                                  {creative.headline}
                                </p>
                              </div>
                              <div data-id="element-11543">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11544">
                                  Call to Action
                                </h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11545">
                                  {creative.cta}
                                </p>
                              </div>
                              <div data-id="element-11546">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11547">
                                  Aspect Ratio
                                </h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11548">
                                  {creative.aspectRatioId === '16_9' ? '16:9' : creative.aspectRatioId === '4_3' ? '4:3' : creative.aspectRatioId === '1_1' ? '1:1' : creative.aspectRatioId}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)}
              </div>
            </TabsContent>
            <TabsContent value="tracking" className="pt-6" data-id="element-11549">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11550">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11551">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11552">
                    Tracking Parameters
                  </h3>
                  <div className="space-y-4" data-id="element-11553">
                    <div data-id="element-11554">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11555">
                        Click Macro Tokens
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11556">
                        {offer.tracking.clickMacroTokens}
                      </p>
                    </div>
                    <div data-id="element-11557">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11558">
                        Postback URL
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white break-all" data-id="element-11559">
                        {offer.tracking.postbackUrl}
                      </p>
                    </div>
                    <div data-id="element-11560">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11561">
                        Webhook Enabled
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11562">
                        {offer.tracking.webhookEnabled ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-11563">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11564">
                        Pixel Type
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11565">
                        {{
                        image: 'Image Pixel',
                        iframe: 'iFrame Pixel',
                        js: 'JavaScript Pixel',
                        s2s: 'Server-to-Server'
                      }[offer.tracking.pixelTypeId] || offer.tracking.pixelTypeId}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11566">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11567">
                    Caps & Priority
                  </h3>
                  <div className="space-y-4" data-id="element-11568">
                    <div data-id="element-11569">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11570">
                        Daily Cap
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11571">
                        {offer.caps.daily.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11572">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11573">
                        Hourly Cap
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11574">
                        {offer.caps.hourly.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-11575">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11576">
                        Frequency Cap
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11577">
                        {offer.caps.frequency}
                      </p>
                    </div>
                    <div data-id="element-11578">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11579">
                        Pacing
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11580">
                        {offer.caps.pacingId === 'even' ? 'Even' : 'ASAP'}
                      </p>
                    </div>
                    <div data-id="element-11581">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11582">
                        Priority
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11583">
                        {offer.priority}
                      </p>
                    </div>
                    <div data-id="element-11584">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11585">
                        Weight
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11586">
                        {offer.weight}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11587">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11588">
                    Compliance
                  </h3>
                  <div className="space-y-4" data-id="element-11589">
                    <div data-id="element-11590">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11591">
                        Adult Flag
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11592">
                        {offer.compliance.adultFlag ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-11593">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11594">
                        Restricted GEOs
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-11595">
                        {offer.compliance.restrictedGeos.map((geo: string) => <span key={geo} className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 rounded" data-id="element-11596">
                            {geo.toUpperCase()}
                          </span>)}
                      </div>
                    </div>
                    <div data-id="element-11597">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11598">
                        Age Gate Required
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11599">
                        {offer.compliance.ageGate ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-11600">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11601">
                        KYC Needed
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11602">
                        {offer.compliance.kycNeeded ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-11603">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11604">
                        Internal Risk Score
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11605">
                        {offer.compliance.internalRiskScore}/5
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11606">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11607">
                    Tracking URL Builder
                  </h3>
                  <div className="space-y-4" data-id="element-11608">
                    <div data-id="element-11609">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11610">
                        Base URL
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white break-all" data-id="element-11611">
                        {offer.baseUrl}
                      </p>
                    </div>
                    <div data-id="element-11612">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11613">
                        Tracking Parameters
                      </h4>
                      <div className="mt-1 space-y-2" data-id="element-11614">
                        {offer.trackingParams && Object.entries(offer.trackingParams).map(([key, value]: [string, any]) => <div key={key} className="flex justify-between" data-id="element-11615">
                                <span className="text-sm text-gray-900 dark:text-white" data-id="element-11616">
                                  {key}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-11617">
                                  {value}
                                </span>
                              </div>)}
                      </div>
                    </div>
                    <div data-id="element-11618">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11619">
                        Full Tracking URL
                      </h4>
                      <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-md" data-id="element-11620">
                        <code className="text-xs text-gray-900 dark:text-gray-300 break-all" data-id="element-11621">
                          {offer.baseUrl}?
                          {Object.entries(offer.trackingParams || {}).map(([key, value]) => `${key}=${value}`).join('&')}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>;
};